(() => {
  if (window.customElements.get('product-hover-zoom')) {
    return;
  }

  const DESKTOP_HOVER_QUERY = '(min-width: 1000px) and (hover: hover) and (pointer: fine)';
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class ProductHoverZoom extends HTMLElement {
    constructor() {
      super();

      this._onPointerOver = this._onPointerOver.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerOut = this._onPointerOut.bind(this);
      this._onContextChange = this._onContextChange.bind(this);
      this._hide = this._hide.bind(this);

      this._sourceByMediaId = new Map();
      this._sourceLoadState = new Map();
      this._active = null;
      this._animationFrame = null;
      this._pendingPointer = null;
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) {
        return;
      }

      this._product = this.closest('.product');
      this._productMedia = this._product?.querySelector('.product__media');
      this._productInfo = this._product?.querySelector('.product__info');

      if (!this._product || !this._productMedia || !this._productInfo) {
        return;
      }

      this._readSources();
      if (this._sourceByMediaId.size === 0) {
        return;
      }

      this._mediaQuery = window.matchMedia(DESKTOP_HOVER_QUERY);
      this._panel = document.createElement('div');
      this._panel.className = 'product-hover-zoom__panel';
      this._panel.hidden = true;
      this._panel.setAttribute('aria-hidden', 'true');
      this._product.appendChild(this._panel);
      this._product.classList.add('product--has-hover-zoom');

      this._productMedia.addEventListener('pointerover', this._onPointerOver);
      this._productMedia.addEventListener('pointermove', this._onPointerMove);
      this._productMedia.addEventListener('pointerout', this._onPointerOut);
      this._productMedia.addEventListener('flickity:slide-changed', this._onContextChange);
      this._productMedia.addEventListener('flickity:slide-settled', this._onContextChange);
      document.addEventListener('variant:change', this._onContextChange);
      document.addEventListener('variant:changed', this._onContextChange);
      window.addEventListener('resize', this._hide, { passive: true });
      window.addEventListener('scroll', this._hide, { passive: true, capture: true });
      window.addEventListener('blur', this._hide);
      this._mediaQuery.addEventListener('change', this._hide);

      this._initialized = true;
      this._preloadSelectedImage();
    }

    disconnectedCallback() {
      if (!this._initialized) {
        return;
      }

      this._hide();
      this._productMedia.removeEventListener('pointerover', this._onPointerOver);
      this._productMedia.removeEventListener('pointermove', this._onPointerMove);
      this._productMedia.removeEventListener('pointerout', this._onPointerOut);
      this._productMedia.removeEventListener('flickity:slide-changed', this._onContextChange);
      this._productMedia.removeEventListener('flickity:slide-settled', this._onContextChange);
      document.removeEventListener('variant:change', this._onContextChange);
      document.removeEventListener('variant:changed', this._onContextChange);
      window.removeEventListener('resize', this._hide);
      window.removeEventListener('scroll', this._hide, true);
      window.removeEventListener('blur', this._hide);
      this._mediaQuery.removeEventListener('change', this._hide);
      this._panel?.remove();
      this._product?.classList.remove('product--has-hover-zoom');
      this._initialized = false;
    }

    _readSources() {
      const sourceElement = this.querySelector('[data-product-hover-zoom-sources]');

      try {
        const sources = JSON.parse(sourceElement?.textContent || '[]');
        sources.forEach(({ mediaId, zoomUrl }) => {
          if (mediaId && zoomUrl) {
            this._sourceByMediaId.set(String(mediaId), zoomUrl);
          }
        });
      } catch (error) {
        console.warn('Unable to parse product hover zoom sources.', error);
      }
    }

    _getSelectedSlide() {
      return this._productMedia.querySelector('.product__media-item.is-selected[data-media-type="image"]:not(.is-filtered)');
    }

    _preloadSelectedImage() {
      const selectedSlide = this._getSelectedSlide();
      const source = selectedSlide ? this._sourceByMediaId.get(selectedSlide.dataset.mediaId) : null;

      if (source) {
        this._loadSource(source);
      }
    }

    _loadSource(source) {
      if (!source) {
        return Promise.resolve(false);
      }

      if (this._sourceLoadState.has(source)) {
        return this._sourceLoadState.get(source);
      }

      const promise = new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = source;
      });

      this._sourceLoadState.set(source, promise);
      return promise;
    }

    _onContextChange() {
      this._hide();
      window.setTimeout(() => this._preloadSelectedImage(), 0);
    }

    _onPointerOver(event) {
      if (!this._mediaQuery.matches) {
        return;
      }

      const wrapper = event.target.closest('.product__media-image-wrapper');
      if (!wrapper || !this._productMedia.contains(wrapper) || wrapper.contains(event.relatedTarget)) {
        return;
      }

      const slide = wrapper.closest('.product__media-item[data-media-id]');
      if (
        !slide ||
        slide.dataset.mediaType !== 'image' ||
        !slide.classList.contains('is-selected') ||
        slide.classList.contains('is-filtered')
      ) {
        return;
      }

      const image = wrapper.querySelector('img');
      if (!image) {
        return;
      }

      this._show(wrapper, slide, image, event.clientX, event.clientY);
    }

    _onPointerMove(event) {
      if (!this._active || !this._active.wrapper.contains(event.target)) {
        return;
      }

      this._pendingPointer = { x: event.clientX, y: event.clientY };
      if (this._animationFrame) {
        return;
      }

      this._animationFrame = window.requestAnimationFrame(() => {
        this._animationFrame = null;
        if (this._pendingPointer) {
          this._updatePosition(this._pendingPointer.x, this._pendingPointer.y);
        }
      });
    }

    _onPointerOut(event) {
      if (!this._active || !this._active.wrapper.contains(event.target)) {
        return;
      }

      if (this._active.wrapper.contains(event.relatedTarget)) {
        return;
      }

      this._hide();
    }

    _show(wrapper, slide, image, clientX, clientY) {
      this._hide();

      const lens = document.createElement('div');
      lens.className = 'product-hover-zoom__lens';
      lens.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(lens);

      const mediaId = slide.dataset.mediaId;
      const zoomSource = this._sourceByMediaId.get(mediaId);
      const fallbackSource = image.currentSrc || image.src;
      this._active = { wrapper, image, lens, mediaId, zoomSource, fallbackSource };
      this._panel.style.backgroundImage = `url("${this._escapeCssUrl(fallbackSource)}")`;
      this._panel.hidden = false;
      this._updatePosition(clientX, clientY);

      this._loadSource(zoomSource).then((loaded) => {
        if (loaded && this._active?.mediaId === mediaId) {
          this._panel.style.backgroundImage = `url("${this._escapeCssUrl(zoomSource)}")`;
        }
      });
    }

    _getRenderedImageRect(image) {
      const elementRect = image.getBoundingClientRect();
      const naturalWidth = image.naturalWidth;
      const naturalHeight = image.naturalHeight;
      const objectFit = window.getComputedStyle(image).objectFit;

      if (
        objectFit !== 'contain' ||
        naturalWidth <= 0 ||
        naturalHeight <= 0 ||
        elementRect.width <= 0 ||
        elementRect.height <= 0
      ) {
        return elementRect;
      }

      const scale = Math.min(elementRect.width / naturalWidth, elementRect.height / naturalHeight);
      const width = naturalWidth * scale;
      const height = naturalHeight * scale;

      return {
        left: elementRect.left + (elementRect.width - width) / 2,
        top: elementRect.top + (elementRect.height - height) / 2,
        right: elementRect.left + (elementRect.width + width) / 2,
        bottom: elementRect.top + (elementRect.height + height) / 2,
        width,
        height,
      };
    }

    _updatePosition(clientX, clientY) {
      if (!this._active || !this._mediaQuery.matches) {
        return;
      }

      const productRect = this._product.getBoundingClientRect();
      const infoRect = this._productInfo.getBoundingClientRect();
      const wrapperRect = this._active.wrapper.getBoundingClientRect();
      const imageRect = this._getRenderedImageRect(this._active.image);
      const ratio = Math.max(Number.parseFloat(this.dataset.zoomRatio) || 2, 1);

      if (imageRect.width <= 0 || imageRect.height <= 0 || infoRect.width <= 0) {
        this._hide();
        return;
      }

      const panelWidth = infoRect.width;
      const panelHeight = imageRect.height;
      const lensWidth = Math.min(imageRect.width, panelWidth / ratio);
      const lensHeight = Math.min(imageRect.height, panelHeight / ratio);
      const pointerX = clamp(clientX - imageRect.left, 0, imageRect.width);
      const pointerY = clamp(clientY - imageRect.top, 0, imageRect.height);
      const lensX = clamp(pointerX - lensWidth / 2, 0, imageRect.width - lensWidth);
      const lensY = clamp(pointerY - lensHeight / 2, 0, imageRect.height - lensHeight);

      Object.assign(this._active.lens.style, {
        left: `${imageRect.left - wrapperRect.left + lensX}px`,
        top: `${imageRect.top - wrapperRect.top + lensY}px`,
        width: `${lensWidth}px`,
        height: `${lensHeight}px`,
      });

      Object.assign(this._panel.style, {
        left: `${infoRect.left - productRect.left}px`,
        top: `${imageRect.top - productRect.top}px`,
        width: `${panelWidth}px`,
        height: `${panelHeight}px`,
        backgroundSize: `${imageRect.width * ratio}px ${imageRect.height * ratio}px`,
        backgroundPosition: `${-lensX * ratio}px ${-lensY * ratio}px`,
      });
    }

    _escapeCssUrl(url) {
      return String(url || '').replace(/["\\\n\r]/g, (character) => `\\${character}`);
    }

    _hide() {
      if (this._animationFrame) {
        window.cancelAnimationFrame(this._animationFrame);
        this._animationFrame = null;
      }

      this._pendingPointer = null;
      this._active?.lens.remove();
      this._active = null;

      if (this._panel) {
        this._panel.hidden = true;
        this._panel.removeAttribute('style');
      }
    }
  }

  window.customElements.define('product-hover-zoom', ProductHoverZoom);
})();
